import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Pressable, ScrollView, useWindowDimensions, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {CourseSummary} from '../../../Hooks/LmsCourseApi/types';
import {
  archiveCourses,
  CourseListRow,
  restoreCourses,
  useCourseList,
  useCreateCourse,
} from '../../../Hooks/LmsCourseApi/useCourseList';

import {
  ArchiveRisk,
  archivedIds,
  archivedOn,
  archivedToast,
  CONFIRM_WORD,
  deleteLabel,
  describeArchiveError,
  knownRisks,
  needsConfirmFrom,
  restoredToast,
  restoreLabel,
  selectedLabel,
} from '../courseArchive';
import {formatDuration} from '../courseFormat';
import {progressCta, publicationChip} from '../courseProgress';
import {describe} from '../courseStatus';
import {focusVisible, motion, ring} from '../editor/ui';
import ArchiveConfirmDialog from './ArchiveConfirmDialog';
import CourseArtwork from './CourseArtwork';
import CourseButton from './CourseButton';
import {
  CheckMark,
  ListButton,
  ListToast,
  ListView,
  SelectAll,
  SelectionBar,
  ViewSwitch,
} from './CourseListControls';
import {prefersReducedMotion} from './prefersReducedMotion';
import {CARD_SHADOW, contentWidth, F, MOBILE_BREAKPOINT, S, STATUS, T} from '../courseTheme';

type Props = {
  onOpen: (uuid: string) => void;
  onCreate?: () => void;
  /** Open the review screen for a finished course. Admins only. */
  onReview?: (uuid: string) => void;
};

const CARD_MIN = 300;
const CARD_MAX = 380;

/**
 * Labels laid over the artwork. A solid 60% scrim, not 35%: white text on the
 * lighter one measured 4.5:1 only where the art was dark, and these images are
 * often a pale sky. On this one white clears 5.7:1 over pure white artwork.
 */
const ART_CHIP = {
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  gap: 4,
  backgroundColor: 'rgba(0,0,0,0.6)',
  borderRadius: 6,
  paddingHorizontal: 8,
  paddingVertical: 4,
};

function Stat({value, label}: {value: string; label: string}) {
  return (
    <View style={{gap: 2}}>
      <CCText style={{fontSize: 18, fontWeight: '600', color: F.brownDark}}>
        {value}
      </CCText>
      <CCText style={{fontSize: 12, fontWeight: '600', color: F.mutedText, textTransform: 'uppercase', letterSpacing: 0.4}}>
        {label}
      </CCText>
    </View>
  );
}

function CourseCard({
  course,
  width,
  onPress,
  onRetry,
  retrying,
  onReview,
  selecting = false,
  checked = false,
  onToggle,
  archived = false,
  onRestore,
  restoring = false,
  reduced,
}: {
  course: CourseListRow;
  width: number;
  onPress: () => void;
  onRetry?: (course: CourseSummary, force: boolean) => void;
  retrying?: boolean;
  onReview?: (uuid: string) => void;
  /** Select mode: the card is a checkbox, and pressing it ticks it. */
  selecting?: boolean;
  checked?: boolean;
  onToggle?: () => void;
  /** A row of the Archived view: hidden from students, not openable. */
  archived?: boolean;
  onRestore?: () => void;
  restoring?: boolean;
  reduced: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const duration = formatDuration(course.duration_seconds);
  // Who taught it is what a learner chooses between masterclasses on. The
  // author is the house name on every row, so it is only the fallback.
  const byline = course.coach?.trim() || course.author;
  // Defaulted, not assumed present: an API that predates the status field
  // would otherwise crash the whole screen on `undefined.toUpperCase()`, and a
  // course that has a book is by definition finished.
  const status = course.status ?? (course.uuid ? 'done' : 'pending');
  // What we may honestly claim about this row, and what to offer about it.
  const verdict = describe(course);
  // A course still generating has no book to open yet, and a failed one has
  // nothing behind it — but both have to be visible, or the admin who started
  // the run has no way back to it and a dead run looks like nothing happened.
  const ready = status === 'done' && !!course.uuid;
  // An archived course is hidden from students until it is restored, so the
  // card does not offer to start it.
  const openable = ready && !archived;
  const archivedWhen = archived ? archivedOn(course.archived_at) : '';
  const stateWord = archived ? 'Archived' : verdict.label;
  // Start, continue or watch again — the student's own progress.
  const cta = progressCta(course.progress);

  const content = (
      <>
      {/* The cover: the course's AI artwork, or the designed tile in the
          brand colour when it has none. It carries the one fact a learner
          decides on — how long it is — and the title. */}
      <CourseArtwork
        title={course.title}
        thumbnail={course.thumbnail}
        cover={course.cover}
        topLeft={
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
            {selecting ? <CheckMark state={checked} size={26} /> : null}
            {archived ? (
              // Icon and word on the 60% scrim: white clears 5.7:1 there.
              <View style={ART_CHIP}>
                <Ionicons name="archive-outline" size={13} color={F.white} />
                <CCText style={{fontSize: 12, fontWeight: '600', color: F.white, letterSpacing: 0.4}}>
                  ARCHIVED
                </CCText>
              </View>
            ) : (
              // Not ready: white chip, STATUS red text (5.4:1). F.wrongFg is a
              // fill colour — as text it measured 2.7:1 on its own pink.
              <View style={[ART_CHIP, ready ? null : {backgroundColor: F.white}]}>
                <CCText
                  style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: ready ? F.white : STATUS.bad.fg,
                    letterSpacing: 0.4,
                  }}>
                  {ready ? publicationChip(course.review_state) : verdict.label.toUpperCase()}
                </CCText>
              </View>
            )}
          </View>
        }
        topRight={
          duration ? (
            <View accessible accessibilityLabel={`Duration ${duration}`} style={ART_CHIP}>
              <Ionicons name="time-outline" size={13} color={F.white} />
              <CCText style={{fontSize: 13, fontWeight: '600', color: F.white, fontVariant: ['tabular-nums']}}>
                {duration}
              </CCText>
            </View>
          ) : null
        }
      />

      <View style={{padding: 20, gap: 16}}>
        <View style={{gap: 6}}>
          {/* Who taught it is the actual basis for choosing between
              masterclasses, and the card was carrying it in the payload
              without ever showing it. */}
          {byline ? (
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <Ionicons name="person-outline" size={15} color={F.brown} />
              <CCText numberOfLines={1} style={{flexShrink: 1, fontSize: 14, fontWeight: '600', color: F.brown}}>
                {byline}
              </CCText>
            </View>
          ) : null}
          {course.description ? (
            <CCText
              numberOfLines={3}
              style={{fontSize: 14, fontWeight: '500', color: F.brown, lineHeight: 21}}>
              {course.description}
            </CCText>
          ) : null}
        </View>

        <View style={{flexDirection: 'row', gap: 28}}>
          <Stat value={String(course.chapter_count)} label="Chapters" />
          <Stat value={String(course.puzzle_count)} label="Puzzles" />
        </View>

        {/* The whole card is the control, so this is deliberately a label and
            not a nested button — it used to look pressable, give no press
            feedback, and be invisible to assistive tech. */}
        {archived ? (
          <View style={{gap: 4}}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <Ionicons name="archive-outline" size={15} color={F.brown} />
              <CCText style={{fontSize: 14, fontWeight: '600', color: F.brownDark}}>
                {archivedWhen}
              </CCText>
            </View>
            <CCText style={{fontSize: 13, fontWeight: '500', color: F.mutedText, lineHeight: 19}}>
              Hidden from students. Restoring brings it back as a draft.
            </CCText>
          </View>
        ) : ready ? (
          <View style={{gap: 10}}>
            {/* How far this student got. No bar on a course never opened:
                an empty track reads as "0% failed", not "new". */}
            {cta.percent > 0 ? (
              <View
                accessible
                accessibilityLabel={`${cta.percent}% done`}
                style={{height: 6, borderRadius: 3, backgroundColor: F.border10, overflow: 'hidden'}}>
                <View
                  testID="course-progress-fill"
                  style={[{height: 6, borderRadius: 3, backgroundColor: F.tealDark}, {width: `${cta.percent}%`}]}
                />
              </View>
            ) : null}
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <CCText
                style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: F.tealDark,
                  letterSpacing: 0.2,
                }}>
                {cta.label}
              </CCText>
              <Ionicons name="arrow-forward" size={15} color={F.tealDark} />
            </View>
          </View>
        ) : (
          <CCText
            style={{
              fontSize: 13,
              fontWeight: '500',
              // A stalled run is not neutral information — it is something
              // that needs a person, and grey is how it gets scrolled past.
              color: verdict.stalled ? STATUS.bad.fg : F.mutedText,
              lineHeight: 19,
            }}>
            {verdict.detail}
          </CCText>
        )}

      </View>
      </>
  );

  // The retry control is a sibling of the pressable region, not a child of
  // it. Nesting a button inside the card's Pressable needs the press kept
  // from bubbling up and opening the course, and every way of doing that —
  // claiming the responder, stopping propagation — swallowed the button's own
  // press instead, leaving a control that rendered and did nothing.
  return (
    <View
      style={[
        {
          width,
          backgroundColor: F.white,
          borderRadius: F.radius.card,
          overflow: 'hidden',
        },
        CARD_SHADOW,
      ]}>
    {selecting ? (
      // In select mode the whole card is the checkbox: the biggest possible
      // target, and one control per course for a screen reader.
      <Pressable
        accessibilityRole="checkbox"
        accessibilityLabel={`${course.title}, ${stateWord}`}
        accessibilityState={{checked}}
        // react-native-web 0.19 drops accessibilityState; only aria-* reaches
        // the DOM.
        {...({'aria-checked': checked} as any)}
        onPress={() => onToggle?.()}
        onFocus={() => setFocused(focusVisible())}
        onBlur={() => setFocused(false)}
        style={({pressed}: any) => [
          {opacity: pressed ? 0.9 : 1, cursor: 'pointer'} as any,
          motion(reduced),
          ring(focused, true),
        ]}>
        {content}
      </Pressable>
    ) : (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={
        (openable
          ? `Open ${course.title}`
          : archived
          ? `${course.title}, ${archivedWhen}`
          : `${course.title} — ${status}`) +
        (byline ? `, by ${byline}` : '') +
        (duration ? `, ${duration}` : '')
      }
      accessibilityState={{disabled: !openable}}
      disabled={!openable}
      onPress={onPress}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed}: any) => [
        {opacity: pressed && openable ? 0.9 : 1} as any,
        motion(reduced),
        ring(focused, true),
      ]}>
      {content}
    </Pressable>
    )}

      {/* Selected: a dark border drawn over the card (5.6:1 against the page
          teal), on top of the tick — never colour alone, and no layout shift. */}
      {selecting && checked ? (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderWidth: 3,
            borderColor: F.brownDark,
            borderRadius: F.radius.card,
          }}
        />
      ) : null}

      {/* Outside the Pressable above, so its press is its own. Offered on a
          finished course too: recordings get re-cut and the detector
          improves, and there was previously no way to rebuild one at all
          without going through the S3 link form. Stepped aside in select
          mode, where a press means "tick this". */}
      {!selecting && archived && onRestore ? (
        <View style={{paddingHorizontal: 20, paddingBottom: 20, flexDirection: 'row'}}>
          <CourseButton
            label={restoring ? 'Restoring…' : 'Restore'}
            variant="review"
            disabled={restoring}
            onPress={onRestore}
          />
        </View>
      ) : null}
      {!selecting && !archived && ((onRetry && verdict.action !== 'none') || (onReview && ready)) ? (
        <View style={{paddingHorizontal: 20, paddingBottom: 20, flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
          {onReview && ready && course.uuid ? (
            <CourseButton
              label="Review"
              onPress={() => onReview(course.uuid as string)}
            />
          ) : null}
          {onRetry && verdict.action !== 'none' ? (
            <CourseButton
              label={retrying ? 'Starting…' : verdict.actionLabel}
              variant="review"
              disabled={!!retrying}
              onPress={() => onRetry(course, verdict.forces)}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );

}

/**
 * Every generated course, as a card grid.
 *
 * Cards are a fixed width rather than full-bleed: one course stretched across
 * a 1300px row reads as a broken layout, and the eye has nothing to anchor on.
 */
export default function CourseListScreen({onOpen, onCreate, onReview}: Props) {
  const [view, setView] = useState<ListView>('active');
  const archivedView = view === 'archived';
  const {data, isLoading, error, refetch} = useCourseList(view);
  const {width: windowWidth} = useWindowDimensions();
  const [reduced] = useState(prefersReducedMotion);
  // The server decides who may generate a course; this only decides whether to
  // offer the button, so it does not become a second, drifting copy of the
  // rule. The POST re-checks.
  const canCreate = !!data?.can_create;
  // Admin controls (the view switch, Select) stay put while the other view
  // loads: `data` is briefly empty then, and the way back must not vanish.
  const adminSeen = useRef(false);
  if (canCreate) adminSeen.current = true;
  const admin = canCreate || adminSeen.current;

  // Courses archived or restored a moment ago, out of the list before the
  // refetch confirms it.
  const [hidden, setHidden] = useState<Set<number>>(() => new Set());
  const courses = (data?.courses ?? []).filter(c => !hidden.has(c.video_course_id));

  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(() => new Set());
  // In list order, so what is sent and what is shown agree.
  const selectedRows = selecting ? courses.filter(c => picked.has(c.video_course_id)) : [];
  const selectedIds = selectedRows.map(c => c.video_course_id);

  const [confirming, setConfirming] = useState<{
    ids: number[];
    risks: ArchiveRisk[];
    typed: boolean;
  } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [toast, setToast] = useState<{
    text: string;
    tone: 'info' | 'bad';
    undoIds?: number[];
  } | null>(null);
  const [undoing, setUndoing] = useState(false);
  const selectRef = useRef<any>(null);

  const leaveSelect = () => {
    setSelecting(false);
    setPicked(new Set());
  };

  const switchView = (next: ListView) => {
    setView(next);
    leaveSelect();
    setHidden(new Set());
    setConfirming(null);
  };

  // A view visited before is served from cache, and a Delete or Restore
  // since then has changed it: fetch it again on the way in.
  const shownView = useRef(view);
  useEffect(() => {
    if (shownView.current === view) return;
    shownView.current = view;
    if (data) refetch();
  }, [view, data, refetch]);

  // Escape leaves select mode — but not while the dialog is open: it closes
  // itself on Escape, and one key should close one thing.
  useEffect(() => {
    if (!selecting || confirming) return undefined;
    const doc = (globalThis as any).document;
    if (!doc?.addEventListener) return undefined;
    const onKey = (e: any) => {
      if (e?.key === 'Escape') leaveSelect();
    };
    doc.addEventListener('keyup', onKey);
    return () => doc.removeEventListener('keyup', onKey);
  }, [selecting, confirming]);

  // Long enough to reach Undo by keyboard; the Archived view is the
  // permanent way back.
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), toast.undoIds ? 10000 : 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  const toggle = (id: number) =>
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setPicked(
      selectedIds.length === courses.length
        ? new Set()
        : new Set(courses.map(c => c.video_course_id)),
    );

  const askToDelete = () => {
    if (!selectedRows.length) return;
    const risks = knownRisks(selectedRows);
    setDialogError(null);
    setConfirming({ids: selectedIds, risks, typed: risks.length > 0});
  };

  // After the bar that opened the dialog is gone, focus would fall to the
  // page; Select is where a keyboard user carries on from.
  const focusSelectSoon = () =>
    setTimeout(() => selectRef.current?.focus?.(), 0);

  const archive = async (confirm?: typeof CONFIRM_WORD) => {
    if (!confirming || busy) return;
    const {ids} = confirming;
    setBusy(true);
    setDialogError(null);
    try {
      const result = confirm ? await archiveCourses(ids, confirm) : await archiveCourses(ids);
      setHidden(prev => new Set([...prev, ...ids]));
      setConfirming(null);
      leaveSelect();
      setToast({
        text: archivedToast(result, ids.length),
        tone: 'info',
        undoIds: archivedIds(result, ids),
      });
      refetch();
      focusSelectSoon();
    } catch (err) {
      const needs = needsConfirmFrom(err);
      if (needs && !confirm) {
        // The server knows something the list did not (published, say):
        // ask for the word, naming what it named.
        setConfirming(prev =>
          prev && {...prev, risks: needs.length ? needs : prev.risks, typed: true},
        );
      } else {
        setDialogError(describeArchiveError(err, 'archive'));
      }
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    const ids = toast?.undoIds;
    if (!ids || undoing) return;
    setUndoing(true);
    try {
      await restoreCourses(ids);
      setHidden(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setToast({text: restoredToast(ids.length), tone: 'info'});
      refetch();
    } catch (err) {
      // Undo stays offered: trying again is the obvious next move.
      setToast({text: describeArchiveError(err, 'restore'), tone: 'bad', undoIds: ids});
    } finally {
      setUndoing(false);
    }
  };

  // Restore needs no typed confirm: nothing is lost by it, and the course
  // comes back as an unpublished draft.
  const restore = async (ids: number[]) => {
    if (!ids.length || busy) return;
    setBusy(true);
    if (ids.length === 1) setRestoringId(ids[0]);
    try {
      await restoreCourses(ids);
      setHidden(prev => new Set([...prev, ...ids]));
      leaveSelect();
      setToast({text: restoredToast(ids.length), tone: 'info'});
      refetch();
    } catch (err) {
      setToast({text: describeArchiveError(err, 'restore'), tone: 'bad'});
    } finally {
      setBusy(false);
      setRestoringId(null);
    }
  };

  const create = useCreateCourse();
  const [retryingId, setRetryingId] = React.useState<number | null>(null);
  const [retryError, setRetryError] = React.useState<string | null>(null);

  /**
   * Start a run again from the card it belongs to.
   *
   * Whether to force is the verdict's call, not this component's. The server
   * only re-dispatches an unfinished job that is `pending` *and* stale —
   * `_is_stale_pending` never expires a `running` row — so a stalled run
   * without force comes back as a 200 carrying the same row, and the button
   * silently does nothing.
   */
  const retry = React.useCallback(
    (course: CourseSummary, force: boolean) => {
      setRetryingId(course.video_course_id);
      setRetryError(null);
      create.mutate(
        {video_course_id: course.video_course_id, force},
        {
          onSuccess: () => {
            setRetryingId(null);
            refetch();
          },
          onError: (err: any) => {
            setRetryingId(null);
            setRetryError(
              err?.response?.data?.error ??
                'Could not start that again. Try once more in a moment.',
            );
          },
        },
      );
    },
    [create, refetch],
  );

  // Leave room for the app's own left nav before working out how many fit.
  const available = Math.max(CARD_MIN, contentWidth(windowWidth) - 48);
  const narrow = available < MOBILE_BREAKPOINT;
  const perRow = Math.max(1, Math.floor(available / CARD_MIN));
  const cardWidth = narrow
    ? Math.min(available, CARD_MAX)
    : Math.min(CARD_MAX, Math.floor((available - (perRow - 1) * 20) / perRow));

  return (
    <View style={{flex: 1, backgroundColor: F.teal}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          paddingHorizontal: 24,
          paddingTop: 28,
          paddingBottom: 4,
          gap: 12,
          flexWrap: 'wrap',
        }}>
        <View style={{gap: 4}}>
          {/* "Masterclass", matching the drawer entry. "Courses" is a
              different screen in the same menu (the School of Chess LMS), and
              two headings with one name is how you get a support ticket. */}
          <CCText style={{fontSize: 28, fontWeight: '600', color: F.white}}>
            Masterclass
          </CCText>
          <CCText style={{fontSize: 14, fontWeight: '500', color: F.white}}>
            {archivedView
              ? courses.length
                ? `${courses.length} archived course${courses.length === 1 ? '' : 's'}`
                : 'Archived courses'
              : courses.length
              ? `${courses.length} course${courses.length === 1 ? '' : 's'} built from masterclass recordings`
              : 'Built automatically from masterclass recordings'}
          </CCText>
        </View>
        {/* flexShrink + maxWidth so the row wraps on a phone: RN does not
            shrink a flex child by default, and these overflowed sideways. */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: S[2],
            flexWrap: 'wrap',
            flexShrink: 1,
            maxWidth: '100%',
          }}>
        {admin ? <ViewSwitch value={view} onChange={switchView} reduced={reduced} /> : null}
        {admin && courses.length > 0 ? (
          <ListButton
            buttonRef={selectRef}
            label="Select"
            icon={selecting ? 'checkbox' : 'checkbox-outline'}
            pressedOn={selecting}
            onPress={() => (selecting ? leaveSelect() : setSelecting(true))}
            a11yHint={
              selecting
                ? 'Leaves select mode'
                : archivedView
                ? 'Choose courses to restore'
                : 'Choose courses to delete'
            }
            onTeal
            reduced={reduced}
          />
        ) : null}
        {selecting && courses.length > 0 ? (
          <SelectAll
            count={courses.length}
            selected={selectedIds.length}
            onPress={toggleAll}
            reduced={reduced}
          />
        ) : null}
        {canCreate && onCreate && !selecting && !archivedView ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Create course"
            onPress={onCreate}
            style={{
              backgroundColor: F.yellow,
              borderBottomWidth: 4,
              borderBottomColor: F.yellowBorder,
              borderRadius: F.radius.button,
              paddingHorizontal: 20,
              paddingVertical: 10,
            }}>
            <CCText
              style={{
                fontSize: 13,
                fontWeight: '600',
                color: F.brown,
                textTransform: 'uppercase',
                letterSpacing: 0.32,
              }}>
              + Create course
            </CCText>
          </Pressable>
        ) : null}
        </View>
      </View>

      {/* The list and the notice share this box, so the notice floats over the
          list and never over the selection bar below it. */}
      <View style={{flex: 1, position: 'relative'}}>
      <ScrollView
        contentContainerStyle={{
          padding: 24,
          paddingTop: 20,
          // Room for the notice over the last row of cards.
          paddingBottom: toast ? 104 : 24,
        }}>
        {isLoading ? <ActivityIndicator color={F.white} /> : null}

        {error ? (
          <View style={{gap: 12, alignItems: 'flex-start'}}>
            <CCText style={{color: F.white, fontSize: 15, fontWeight: '600'}}>
              Could not load courses.
            </CCText>
            {/* Without this the only way out of a failed fetch is reloading
                the browser. */}
            <CourseButton label="Try again" onPress={() => refetch()} />
          </View>
        ) : null}

        {!isLoading && !error && archivedView && courses.length > 0 ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: S[2],
              alignSelf: 'flex-start',
              maxWidth: 640,
              backgroundColor: F.white,
              borderRadius: 10,
              paddingHorizontal: S[4],
              paddingVertical: S[3],
              marginBottom: S[5],
            }}>
            <Ionicons name="information-circle-outline" size={18} color={F.brownDark} />
            <CCText style={{flex: 1, fontSize: T.sm + 1, fontWeight: '500', color: F.brownDark, lineHeight: 20}}>
              Archived courses are hidden from students. Restoring one brings it
              back as a draft — publish it again from Review.
            </CCText>
          </View>
        ) : null}

        {!isLoading && !error && archivedView && courses.length === 0 ? (
          <View
            style={[
              {
                backgroundColor: F.white,
                borderRadius: F.radius.card,
                padding: 28,
                maxWidth: 520,
                gap: 8,
              },
              CARD_SHADOW,
            ]}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2]}}>
              <Ionicons name="archive-outline" size={20} color={F.brownDark} />
              <CCText style={{fontSize: 18, fontWeight: '600', color: F.brownDark}}>
                Nothing archived
              </CCText>
            </View>
            <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown, lineHeight: 22}}>
              Courses you delete move here, and you can restore them from here.
            </CCText>
          </View>
        ) : null}

        {!isLoading && !error && !archivedView && courses.length === 0 ? (
          <View
            style={[
              {
                backgroundColor: F.white,
                borderRadius: F.radius.card,
                padding: 28,
                maxWidth: 520,
                gap: 8,
              },
              CARD_SHADOW,
            ]}>
            <CCText style={{fontSize: 18, fontWeight: '600', color: F.brownDark}}>
              No courses yet
            </CCText>
            <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown, lineHeight: 22}}>
              {admin
                ? 'Generate one from a masterclass recording and it will appear here.'
                : 'New masterclass courses will appear here as soon as they are published.'}
            </CCText>
          </View>
        ) : null}

        {retryError ? (
          <CCText
            style={{color: F.white, fontSize: 14, fontWeight: '600', marginBottom: 12}}>
            {retryError}
          </CCText>
        ) : null}

        <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 20}}>
          {courses.map(course => (
            <CourseCard
              // Keyed by the recording, not the book uuid: a course that has
              // not finished generating has no uuid yet, so two in-flight runs
              // would collide on a null key.
              key={course.video_course_id}
              course={course}
              width={cardWidth}
              onPress={() => course.uuid && onOpen(course.uuid)}
              onRetry={canCreate ? retry : undefined}
              retrying={retryingId === course.video_course_id}
              onReview={canCreate ? onReview : undefined}
              selecting={selecting}
              checked={picked.has(course.video_course_id)}
              onToggle={() => toggle(course.video_course_id)}
              archived={archivedView}
              onRestore={canCreate ? () => restore([course.video_course_id]) : undefined}
              restoring={restoringId === course.video_course_id}
              reduced={reduced}
            />
          ))}
        </View>
      </ScrollView>

      {toast ? (
        <ListToast
          text={toast.text}
          tone={toast.tone}
          onUndo={toast.undoIds ? undo : undefined}
          undoing={undoing}
          onDismiss={() => setToast(null)}
          reduced={reduced}
        />
      ) : null}
      </View>

      {selecting ? (
        <SelectionBar
          count={selectedIds.length}
          summary={selectedLabel(selectedIds.length)}
          actionLabel={
            archivedView
              ? busy
                ? 'Restoring…'
                : restoreLabel(selectedIds.length)
              : deleteLabel(selectedIds.length)
          }
          actionIcon={archivedView ? 'arrow-undo-outline' : 'trash-outline'}
          variant={archivedView ? 'accent' : 'danger'}
          busy={busy}
          onAction={archivedView ? () => restore(selectedIds) : askToDelete}
          onCancel={leaveSelect}
          reduced={reduced}
        />
      ) : null}

      {confirming ? (
        <ArchiveConfirmDialog
          count={confirming.ids.length}
          risks={confirming.risks}
          typed={confirming.typed}
          busy={busy}
          error={dialogError}
          onConfirm={archive}
          onCancel={() => setConfirming(null)}
          reduced={reduced}
        />
      ) : null}
    </View>
  );
}
